import { get, post } from '@aiptyg/api-client';
import type {
  PayoutReconcileReportDTO,
  PayoutReconcileRunResultDTO,
  WithdrawalDetailDTO,
  WithdrawalListResponseDTO,
  WithdrawalDTO,
  WithdrawalSortOrder,
} from '../types/withdrawals.types';

export function listWithdrawals(params: {
  marketId: string;
  page: number;
  pageSize: number;
  userId: string;
  userKeyword: string;
  status: string;
  payoutProvider: string;
  startTime: string;
  endTime: string;
  order: WithdrawalSortOrder;
}): Promise<WithdrawalListResponseDTO> {
  const qs = new URLSearchParams();
  qs.set('market_id', params.marketId);
  qs.set('page', String(params.page));
  qs.set('page_size', String(params.pageSize));
  qs.set('order', params.order);
  if (params.userId) qs.set('user_id', params.userId);
  if (params.userKeyword) qs.set('user_keyword', params.userKeyword);
  if (params.status) qs.set('status', params.status);
  if (params.payoutProvider) qs.set('payout_provider', params.payoutProvider);
  if (params.startTime) qs.set('start_time', new Date(params.startTime).toISOString());
  if (params.endTime) qs.set('end_time', new Date(params.endTime).toISOString());
  return get<WithdrawalListResponseDTO>(`/v1/admin/withdrawals?${qs.toString()}`);
}

export function reviewWithdrawal(
  withdrawalId: number,
  action: 'approve' | 'reject',
  reviewNote: string,
  options?: {
    forceManual?: boolean;
  },
): Promise<WithdrawalDTO> {
  return post<WithdrawalDTO>('/v1/admin/withdrawals/review', {
    withdrawal_id: withdrawalId,
    action,
    review_note: reviewNote,
    force_manual: Boolean(options?.forceManual),
  });
}

export function reviewWithdrawals(
  withdrawalIds: number[],
  action: 'approve' | 'reject',
  reviewNote: string,
  options?: {
    forceManual?: boolean;
  },
): Promise<{ items: WithdrawalDTO[] }> {
  return post<{ items: WithdrawalDTO[] }>('/v1/admin/withdrawals/review-batch', {
    withdrawal_ids: withdrawalIds,
    action,
    review_note: reviewNote,
    force_manual: Boolean(options?.forceManual),
  });
}

export function updateWithdrawalTarget(params: {
  withdrawalId: number;
  channel: 'bank_card' | 'alipay';
  accountName: string;
  bankName?: string;
  bankCardNumber?: string;
  alipayAccount?: string;
}): Promise<WithdrawalDTO> {
  return post<WithdrawalDTO>('/v1/admin/withdrawals/update-target', {
    withdrawal_id: params.withdrawalId,
    channel: params.channel,
    account_name: params.accountName,
    bank_name: params.bankName ?? '',
    bank_card_number: params.bankCardNumber ?? '',
    alipay_account: params.alipayAccount ?? '',
  });
}

export function getWithdrawalDetail(withdrawalId: number): Promise<WithdrawalDetailDTO> {
  return get<WithdrawalDetailDTO>(`/v1/admin/withdrawals/${withdrawalId}`);
}

export function startPayout(withdrawalId: number): Promise<WithdrawalDTO> {
  return post<WithdrawalDTO>('/v1/admin/withdrawals/start-payout', {
    withdrawal_id: withdrawalId,
  });
}

export function retryPayout(withdrawalId: number): Promise<WithdrawalDTO> {
  return post<WithdrawalDTO>('/v1/admin/withdrawals/retry-payout', {
    withdrawal_id: withdrawalId,
  });
}

export function queryPayout(withdrawalId: number): Promise<WithdrawalDTO> {
  return post<WithdrawalDTO>('/v1/admin/withdrawals/query-payout', {
    withdrawal_id: withdrawalId,
  });
}

export function switchToManual(withdrawalId: number): Promise<WithdrawalDTO> {
  return post<WithdrawalDTO>('/v1/admin/withdrawals/switch-manual', {
    withdrawal_id: withdrawalId,
  });
}

export function getPayoutReconcileReport(params: {
  staleMinutes: number;
  limit: number;
}): Promise<PayoutReconcileReportDTO> {
  const qs = new URLSearchParams();
  if (params.staleMinutes > 0) qs.set('stale_minutes', String(params.staleMinutes));
  if (params.limit > 0) qs.set('limit', String(params.limit));
  return get<PayoutReconcileReportDTO>(`/v1/admin/withdrawals/reconcile-report?${qs.toString()}`);
}

export function runPayoutReconcile(params: {
  staleMinutes: number;
  limit: number;
}): Promise<PayoutReconcileRunResultDTO> {
  return post<PayoutReconcileRunResultDTO>('/v1/admin/withdrawals/reconcile-run', {
    stale_minutes: params.staleMinutes,
    limit: params.limit,
  });
}

export function settleWithdrawal(withdrawalId: number): Promise<WithdrawalDTO> {
  return settleWithdrawalWithPayload(withdrawalId, {});
}

export function settleWithdrawalWithPayload(
  withdrawalId: number,
  payload: {
    settlementNote?: string;
    paymentProofPath?: string;
  },
): Promise<WithdrawalDTO> {
  return post<WithdrawalDTO>('/v1/admin/withdrawals/settle', {
    withdrawal_id: withdrawalId,
    settlement_note: payload.settlementNote ?? '',
    payment_proof_data_url: payload.paymentProofPath ?? '',
  });
}
